import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useSessionFeatureState } from '@hooks/storeHooks/useSessionState';
import { analytics } from '@utils/analytics';
import { getInitialsFromEmail, isAdmin } from '@utils/authUtils.ts';
import { extraQueryParams } from '@utils/constants';
import { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { Link } from 'react-router-dom';
import { helpCenterUrl } from '../../config';
import Icon from '../Icon';
import MinutesDisplay from '../MinutesDisplay';

interface UserDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  avatarRef?: React.RefObject<HTMLDivElement | null>;
}

export const UserDropdown = memo(
  ({ isOpen, onClose, user, avatarRef }: UserDropdownProps) => {
    const { t } = useTranslation();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const auth = useAuth();
    const { secondsBalance, creditBalance } = useSessionFeatureState();
    const { blobUrlEnabled, setBlobUrlEnabled } = useCommonState();

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;

        // Don't close if clicking on the dropdown itself
        if (dropdownRef.current && dropdownRef.current.contains(target)) {
          return;
        }

        // Don't close if clicking on the avatar (let the avatar handle the toggle)
        if (avatarRef?.current && avatarRef.current.contains(target)) {
          return;
        }

        // Close the dropdown for any other outside clicks
        onClose();
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen, onClose, avatarRef]);

    if (!isOpen) return null;

    const getUserName = () => {
      const profile = user?.profile;
      if (!profile) return null;

      const firstName = (profile.firstName as string) || '';
      const lastName = (profile.lastName as string) || '';

      if (firstName && lastName) {
        return `${firstName} ${lastName}`;
      }

      // Fallback to other profile properties
      const fallbackFirstName = profile.given_name || profile.first_name || '';
      const fallbackLastName = profile.family_name || profile.last_name || '';

      if (fallbackFirstName && fallbackLastName) {
        return `${fallbackFirstName} ${fallbackLastName}`;
      }

      // Return null if first name and last name are not available
      return null;
    };

    const getUserEmail = () => {
      return user?.profile?.email || user?.profile?.preferred_username || '';
    };

    const getUserInitials = () => {
      const profile = user?.profile;
      if (!profile) return 'U';

      const userFirstName = (profile.firstName as string) || '';
      const userLastName = (profile.lastName as string) || '';
      const email = profile.email || profile.preferred_username || '';
      const nameAbbr =
        userFirstName && userLastName
          ? `${userFirstName[0]}${userLastName[0]}`.toUpperCase()
          : getInitialsFromEmail(email);
      if (nameAbbr) {
        return nameAbbr;
      }

      return 'U';
    };

    const userName = getUserName();

    return (
      <div
        ref={dropdownRef}
        className="absolute top-full right-0 mt-2 w-80 bg-black/90 backdrop-blur-md border border-[#fcfcfc]/20 rounded-2xl shadow-2xl z-50">
        {/* User Info Section */}
        <div className="p-4 border-b border-[#fcfcfc]/10">
          <div className="flex items-center gap-4">
            <div
              className="flex items-center justify-center rounded-full text-black font-semibold select-none w-10 h-10 text-xl shrink-0"
              style={{
                background: '#FFFFFF',
              }}>
              {getUserInitials()}
            </div>
            <div className="flex-1 text-left min-w-0">
              {userName && (
                <div className="text-lg font-semibold text-[#fcfcfc] font-[NapsterSans]">
                  {userName}
                </div>
              )}
              <div className="text-sm text-[#fcfcfc]/70 font-[NapsterSans] truncate">
                {getUserEmail()}
              </div>
            </div>
          </div>
        </div>

        {/* Minutes Balance Section */}
        <div className="px-4 py-4 border-b border-[#fcfcfc]/10">
          <MinutesDisplay
            balance={secondsBalance ?? 0}
            creditBalance={creditBalance ?? 0}
            isLowBalance={secondsBalance !== null && secondsBalance < 300} // Less than 5 minutes
            showBuyButton
            variant="full"
          />
        </div>

        {/* Menu Items */}
        <div className="py-2">
          <Link
            to="/account"
            onClick={onClose}
            className="w-full flex items-center gap-3 px-4 py-3 text-[#fcfcfc] hover:bg-[#fcfcfc]/5 transition-colors duration-200 font-[NapsterSans]">
            <i className="fa-solid fa-user  text-[16px]" />
            {t('userMenu.myAccount')}
          </Link>
          <Link
            to="/pricing"
            onClick={onClose}
            className="w-full flex items-center gap-3 px-4 py-3 text-[#fcfcfc] hover:bg-[#fcfcfc]/5 transition-colors duration-200 font-[NapsterSans]">
            <i className="fa-solid fa-tag text-[16px]" />
            {t('userMenu.pricing')}
          </Link>

          {isAdmin(user) && (
            <>
              <a
                href="/cms"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center gap-3 px-4 py-3 text-[#fcfcfc] hover:bg-[#fcfcfc]/5 transition-colors duration-200 font-[NapsterSans]"
                onClick={onClose}>
                <Icon name="settings" size={20} color="#fcfcfc" />
                <span className="text-sm font-medium">{t('userMenu.cmsAdmin')}</span>
              </a>

              {/* Blob Url Toggle */}
              <div className="w-full flex items-center justify-between px-4 py-3 text-[#fcfcfc] hover:bg-[#fcfcfc]/5 transition-colors duration-200 font-[NapsterSans]">
                <div className="flex items-center gap-3">
                  <Icon name="link" size={20} color="#fcfcfc" />
                  <span className="text-sm font-medium">{t('userMenu.blobUrl')}</span>
                </div>
                <button
                  onClick={() => setBlobUrlEnabled(!blobUrlEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    blobUrlEnabled ? 'bg-blue-600' : 'bg-gray-600'
                  }`}>
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      blobUrlEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </>
          )}

          <button
            className="w-full flex items-center gap-3 px-4 py-3 text-[#fcfcfc] hover:bg-[#fcfcfc]/5 transition-colors duration-200 font-[NapsterSans]"
            onClick={() => {
              window.open(helpCenterUrl, '_blank', 'noopener,noreferrer');
              onClose();
            }}>
            <Icon name="help" size={20} color="#fcfcfc" />
            <span className="text-sm font-medium">{t('userMenu.help')}</span>
          </button>

          <button
            className="w-full flex items-center gap-3 px-4 py-3 text-[#fcfcfc] hover:bg-[#fcfcfc]/5 transition-colors duration-200 font-[NapsterSans]"
            onClick={async () => {
              try {
                analytics.logEvent('user_logged_out');
                localStorage.removeItem('napster_platform');
                await auth.signoutRedirect({
                  extraQueryParams,
                });
                await auth.removeUser();
                console.log('Successfully logged out');
              } catch (error) {
                console.error('Failed to log out:', error);
              }
              onClose();
            }}>
            <Icon name="arrow-right" size={20} color="#fcfcfc" />
            <span className="text-sm font-medium">{t('userMenu.signOut')}</span>
          </button>
        </div>
      </div>
    );
  },
);

UserDropdown.displayName = 'UserDropdown';

export default UserDropdown;
